import { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import type { CountDiscrepancy, OverviewReceiving } from '@/api/orders';
import { fmtDate } from './lib';

type Row = CountDiscrepancy & { rcv: OverviewReceiving };

/**
 * Print color per kind: red = units missing, blue = more than billed.
 *
 * `not_invoiced` is absent, not forgotten: that kind cannot reach this sheet. It
 * is
 * emitted only when qtyInvoiced is null, which forces one of the unmatched_*
 * causes, every one of which routes to capture_lines / source_invoice / accept /
 * review, never to the `settle` action the Decide table (and so this sheet)
 * prints from. It is an invoice-reading question, not a count question, so
 * briefing the warehouse on an amber it will never see was noise.
 */
const KIND_TONE_CLASS: Record<string, string> = {
  short: 'tone-missing',
  not_received: 'tone-missing',
  over: 'tone-over',
};

/**
 * Locations sort as plain strings ("A-RRR-HHH" is zero-padded), which walks the
 * warehouse aisle by aisle. Rows with no location — no resolved pid, or the
 * product is unshelved — go to the end rather than sending someone to
 * aisle "—".
 */
function byLocation(locOf: (row: Row) => string | null) {
  return (a: Row, b: Row): number => {
    const la = locOf(a);
    const lb = locOf(b);
    if (la && lb) return la.localeCompare(lb);
    if (la) return -1;
    if (lb) return 1;
    return (a.desc || '').localeCompare(b.desc || '');
  };
}

function identityText(row: Row): string {
  return [row.itemnumber, row.vendorSku]
    .filter(Boolean)
    .join(' · ');
}

/**
 * The paper version of the Decide view — what gets handed to the warehouse to
 * verify counts. Renders into a portal OUTSIDE #root: while it is mounted, the
 * print stylesheet (index.css, `body.print-count-sheet`) hides the app and
 * shows only this sheet, so the browser's print output is the sheet alone.
 * On screen it renders nothing visible (`.count-sheet` is display:none).
 *
 * Mounting triggers window.print() once the DOM has painted; the afterprint
 * event unmounts it again whether the user printed or cancelled. Locations are
 * deliberately print-only — on screen the table links to the product instead.
 *
 * What the sheet ASKS is a verdict, not a tally. Someone may pick this up a
 * week after the receiving, by which time we have sold against it, so "how many
 * are on the shelf" is not comparable to what was received and answers nothing
 * on its own. The write-in columns therefore ask whether the RECEIVED quantity
 * was right — with the shelf, overstock and sales since as evidence — and what,
 * if anything, they changed as a result.
 *
 * `locations` is pid -> CURRENT shelf location, fetched live by the caller the
 * moment the sheet was requested. The mirrored row.location is deliberately
 * ignored here: it is a snapshot from the last orders run, and a printed walk
 * order must reflect where products are today, not where they were shelved
 * when the receiving was mirrored.
 */
export function PrintableCountSheet({ rows, locations, onDone }: {
  rows: Row[];
  locations: Record<string, string>;
  onDone: () => void;
}) {
  const locOf = useMemo(
    () => (row: Row) => (row.pid != null ? locations[String(row.pid)] ?? null : null),
    [locations]
  );

  const groups = useMemo(() => {
    const map = new Map<string, { rcv: OverviewReceiving; rows: Row[] }>();
    for (const row of rows) {
      const entry = map.get(row.rcv.receiptId) || { rcv: row.rcv, rows: [] };
      entry.rows.push(row);
      map.set(row.rcv.receiptId, entry);
    }
    for (const entry of map.values()) entry.rows.sort(byLocation(locOf));
    // Vendor A–Z on paper: the person counting walks per vendor delivery, and
    // the screen's worst-value-first order means nothing on the floor.
    return [...map.values()].sort((a, b) =>
      a.rcv.vendor.localeCompare(b.rcv.vendor) || a.rcv.receivingId - b.rcv.receivingId);
  }, [rows, locOf]);

  useEffect(() => {
    document.body.classList.add('print-count-sheet');
    window.addEventListener('afterprint', onDone);
    // Two frames so the portal content is painted before the print dialog
    // freezes the page — printing on mount catches a blank sheet otherwise.
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => window.print());
    });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('afterprint', onDone);
      document.body.classList.remove('print-count-sheet');
    };
  }, [onDone]);

  return createPortal(
    <div className="count-sheet">
      <header>
        <h1>Receiving count check</h1>
        <div className="count-sheet-meta">
          Printed {new Date().toLocaleString()} · {rows.length} item{rows.length === 1 ? '' : 's'} across{' '}
          {groups.length} receiving{groups.length === 1 ? '' : 's'} · shelf locations current as of printing
        </div>
        <div className="count-sheet-legend">
          <span className="count-sheet-chip tone-missing">red — units missing (short / none received)</span>
          <span className="count-sheet-chip tone-over">blue — more arrived than billed</span>
        </div>
      </header>

      {groups.map(({ rcv, rows: group }) => (
        <section key={rcv.receiptId} className="count-sheet-group">
          <table>
            {/* Column widths live here, not on the <th>s: the table is
                table-layout:fixed (so a long description can never squeeze the
                write-in columns to nothing) and fixed layout reads its widths
                from the first row — which is the colspanning caption below.
                Percentages for the write-in columns so they hold their share
                whatever margins the print dialog is set to. */}
            <colgroup>
              <col style={{ width: '60pt' }} />
              <col />
              <col style={{ width: '32pt' }} />
              <col style={{ width: '32pt' }} />
              <col style={{ width: '50pt' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '20%' }} />
            </colgroup>
            {/* The receiving title lives INSIDE thead, not in an <h2> above the
                table. As a sibling block it was a separate break opportunity —
                `break-after: avoid` is widely ignored — so the title printed
                alone at the foot of a page with the column header and rows
                stranded on the next. Inside the row group it cannot separate
                from the columns. Note the thead is demoted to a plain row group
                in print (see index.css): it must print ONCE, never repeating on
                a continuation page. */}
            <thead>
              <tr className="count-sheet-caption">
                <th colSpan={7}>
                  {/* Brand and receiving number carry the line — they are what
                      someone matches against a carton and types back into
                      acot-db. The release name and date are context, not
                      identity, so they drop to a subheader. */}
                  <span className="count-sheet-caption-main">
                    {rcv.vendor} <span className="count-sheet-caption-num">#{rcv.receivingId}</span>
                  </span>
                  {(rcv.label || rcv.dateCreated) && (
                    <span className="count-sheet-caption-sub">
                      {[rcv.label, rcv.dateCreated ? fmtDate(rcv.dateCreated) : null]
                        .filter(Boolean).join(' · ')}
                    </span>
                  )}
                </th>
              </tr>
              <tr>
                <th className="col-loc">Location</th>
                <th>Item</th>
                <th className="col-num">Inv</th>
                <th className="col-num">Rec</th>
                <th className="col-diff">Diff</th>
                <th className="col-verify">Is received count right?</th>
                <th className="col-notes">Notes</th>
              </tr>
            </thead>
            <tbody>
              {group.map((row) => (
                <tr key={row.key}>
                  <td className="col-loc">{locOf(row) || '—'}</td>
                  <td>
                    {row.desc}
                    <div className="count-sheet-ident">{identityText(row)}</div>
                  </td>
                  <td className="col-num">{row.qtyInvoiced}</td>
                  <td className="col-num">{row.qtyReceived}</td>
                  <td className="col-diff">
                    <span className={`count-sheet-chip ${KIND_TONE_CLASS[row.kind] || ''}`}>
                      {row.difference > 0 ? `+${row.difference}` : row.difference}
                    </span>
                  </td>
                  {/* A verdict, not a number: the shelf quantity a week later
                      answers a different question than the one we are asking. */}
                  {/* Two boxes, one blank. There is no "can't tell" option and
                      no "nothing changed" box: an unsure counter leaves it
                      blank, which says the same thing, and a box nobody ever
                      ticks is a box everybody has to read. */}
                  <td className="col-verify">
                    <div><span className="count-sheet-box" /> yes</div>
                    <div>
                      <span className="count-sheet-box" /> no —{' '}
                      <span className="count-sheet-rule" />
                    </div>
                  </td>
                  <td className="col-notes">
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
    </div>,
    document.body
  );
}
